/**
 * Stats — pure derivations over a Work[] snapshot.
 *
 * Mirrors `mobile/src/screens/WorkListScreen.tsx#calculateStats`
 * (lines 527–551 at the time of writing) so the numbers an admin
 * sees on `/stats` match the bottom-sheet they see on mobile.
 *
 * Why a separate module:
 *   - The route surface (`web/src/routes/Stats.tsx`) stays JSX-only.
 *     The math is testable in isolation without needing happy-dom +
 *     React Testing Library.
 *   - A future "stats in CommandPalette" or "stats in PDF report"
 *     consumer can re-use the same function without dragging the
 *     whole route component along.
 *
 * Edge handling:
 *   - Empty list → all counters return 0, `topClient` and
 *     `avgTimeMs` are 0 / null. The route renders an empty-state
 *     card on top of these zeros, but the function never throws.
 *   - Archived works are excluded (mobile parity). Pass already-
 *     loaded works including archived if you want a different
 *     filter — the function only looks at `archived === true`.
 *   - `totalTime` is treated as "0 means missing" so a
 *     mid-completion work without the field doesn't drag the
 *     average toward zero. Mobile uses the same heuristic.
 *   - `clientName` matching for `topClient` is case-insensitive
 *     and trimmed so "Pepe Pérez" and "pepe pérez " group together.
 *     Mirrors `getClients()` semantics in `web/src/store/db.ts`.
 */
import type { Work } from '../types';

export interface WorkStats {
  /** Total active (non-archived) works. */
  total: number;
  /** Counts by status — these sum to `total`. */
  pendingCount: number;
  inProgressCount: number;
  completedCount: number;
  /** Works with `createdAt` inside the current calendar month. */
  createdThisMonth: number;
  /** Works completed (`status==='completed'`) inside the current month. */
  completedThisMonth: number;
  /** Works with `createdAt` inside the last 7 days (rolling). */
  createdThisWeek: number;
  /** Works completed inside the last 7 days (rolling). */
  completedThisWeek: number;
  /** Mean of `totalTime` over completed works that have it set; ms. */
  avgTimeMs: number;
  /** Top client by frequency. `null` when no client appears on any work. */
  topClient: { name: string; count: number } | null;
}

/** Compute the stats panel's KPIs from an in-memory works list.
 *
 *  `now` is injectable so tests can pin "today" deterministically and
 *  not depend on the wall clock when seeding fixtures. Defaults to
 *  `Date.now()` — the route always passes the default. */
export function computeWorkStats(works: ReadonlyArray<Work>, now: number = Date.now()): WorkStats {
  const active = works.filter((w) => !w.archived);

  const monthStart = new Date(new Date(now).getFullYear(), new Date(now).getMonth(), 1).getTime();
  // Rolling 7 days — mobile doesn't show this today but the data is
  // free and an admin glance at "this week" is the highest-leverage
  // dashboard signal. See spec 217 §4.1 — it lists "this week / this
  // month" as both desirable.
  const weekStart = now - 7 * 24 * 60 * 60 * 1000;

  let pendingCount = 0;
  let inProgressCount = 0;
  let completedCount = 0;
  let createdThisMonth = 0;
  let completedThisMonth = 0;
  let createdThisWeek = 0;
  let completedThisWeek = 0;
  let timeSum = 0;
  let timeSamples = 0;

  // Single pass — works lists in the field stay under ~500 rows for the
  // current cohort, but a single `for-of` is hash-cheap and readable
  // even at 10x that.
  for (const w of active) {
    if (w.status === 'pending') pendingCount++;
    else if (w.status === 'in_progress') inProgressCount++;
    else if (w.status === 'completed') completedCount++;

    if (w.createdAt >= monthStart) createdThisMonth++;
    if (w.createdAt >= weekStart) createdThisWeek++;
    if (w.status === 'completed') {
      if (w.updatedAt >= monthStart) completedThisMonth++;
      if (w.updatedAt >= weekStart) completedThisWeek++;
      if (typeof w.totalTime === 'number' && w.totalTime > 0) {
        timeSum += w.totalTime;
        timeSamples++;
      }
    }
  }

  const avgTimeMs = timeSamples > 0 ? timeSum / timeSamples : 0;
  const topClient = computeTopClient(active);

  return {
    total: active.length,
    pendingCount,
    inProgressCount,
    completedCount,
    createdThisMonth,
    completedThisMonth,
    createdThisWeek,
    completedThisWeek,
    avgTimeMs,
    topClient,
  };
}

function computeTopClient(active: ReadonlyArray<Work>): WorkStats['topClient'] {
  const counts = new Map<string, { display: string; count: number }>();
  for (const w of active) {
    const raw = w.clientName?.trim();
    if (!raw) continue;
    const key = raw.toLocaleLowerCase('es');
    const prev = counts.get(key);
    if (prev) {
      prev.count++;
    } else {
      counts.set(key, { display: raw, count: 1 });
    }
  }
  let best: { display: string; count: number } | null = null;
  for (const entry of counts.values()) {
    if (!best || entry.count > best.count) best = entry;
  }
  return best ? { name: best.display, count: best.count } : null;
}

/** Pretty-print an avgTime in ms as "Xh Ym" — matches mobile.
 *
 *  Returns "—" for 0 / negative input so the route can blindly
 *  render the value without an extra null check. */
export function formatDurationMs(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  const hours = Math.floor(ms / 3_600_000);
  const minutes = Math.round((ms % 3_600_000) / 60_000);
  if (hours === 0) return `${minutes}m`;
  return `${hours}h ${minutes}m`;
}
